import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import useCurrentUser from "../hooks/useCurrentUser";
import { useOrderApproval } from "../hooks/useOrderApproval";
import { STATUS_CONFIG, ORDER_STATUSES } from "../constants/statusConfig";
import { logHistory } from "../utils/logHistory";
import StatCard from "./inventory/StatCard";
import { Package, Truck, Clock, AlertTriangle, Search, Filter, Trash2, CheckCircle, XCircle } from "lucide-react";

export default function ClientOrderAdmin() {
  const { clientId, role, loading, uid } = useCurrentUser();
  const [orders, setOrders] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [showRejectPrompt, setShowRejectPrompt] = useState(null);
  const [rejectMessage, setRejectMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const { approveOrder } = useOrderApproval();

  useEffect(() => {
    if (!clientId || role !== "client") return;

    const q = query(collection(db, "orders"), where("companyId", "==", clientId));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const fetched = snapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          docId: docSnap.id,
        }));
        setOrders(fetched);
      }
    );
    return () => unsub();
  }, [clientId, role]);

  if (loading) return <p className="p-8 text-slate-500">Loading orders...</p>;
  if (!clientId || role !== "client") {
    return <p className="p-8 text-red-500">Access denied: client account only.</p>;
  }

  const getTotalValue = (items = []) =>
    items.reduce((sum, item) => sum + (item.qty || 0) * (item.price || 0), 0);

  const updateStatus = async (order, newStatus) => {
    const orderRef = doc(db, "orders", order.docId);
    setLoadingId(order.docId);

    try {
      if (newStatus === "Approved") {
        await approveOrder(order, clientId);
      } else {
        await updateDoc(orderRef, { status: newStatus });
      }

      await logHistory({
        clientId,
        orderId: order.docId,
        userId: uid,
        role,
        action: "Status Changed",
        from: order.status,
        to: newStatus,
      });
    } catch (err) {
      console.error("❌ Status update failed:", err.message);
    }

    setLoadingId(null);
  };

  const confirmRejection = async () => {
    try {
      const ref = doc(db, "orders", showRejectPrompt);
      await updateDoc(ref, {
        status: "Rejected",
        rejectionReason: rejectMessage,
      });

      await logHistory({
        clientId,
        docId: showRejectPrompt,
        user: { uid, role },
        action: "STATUS_UPDATE",
        details: { from: "Pending", to: "Rejected", reason: rejectMessage },
      });
    } catch (err) {
      console.error("❌ Reject failed:", err.message);
    }

    setShowRejectPrompt(null);
    setRejectMessage("");
  };

  const deleteOrder = async (order) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      await deleteDoc(doc(db, "orders", order.docId));
      await logHistory({
        clientId,
        docId: order.docId,
        user: { uid, role },
        action: "ORDER_DELETED",
        details: { customer: order.customer },
      });
    } catch (err) {
      console.error("❌ Delete failed:", err.message);
    }
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || {};
    return (
      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full inline-flex items-center gap-1.5 ${status === 'Approved' ? 'bg-emerald-100 text-emerald-800' :
        status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
          status === 'Rejected' ? 'bg-red-100 text-red-800' :
            'bg-slate-100 text-slate-800'
        }`}>
        {status}
      </span>
    );
  };

  const filteredOrders = orders.filter((o) => {
    const matchesSearch =
      o.docId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.customer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = statusFilter === "All" || o.status === statusFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900 font-serif">Order Administration</h1>
          <p className="text-slate-500 mt-1">Manage, approve, and track client orders.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          label="Total Orders"
          value={orders.length}
          icon={Package}
          colorClass="bg-blue-500"
        />
        <StatCard
          label="Shipped"
          value={orders.filter(o => o.status === "Shipped").length}
          icon={Truck}
          colorClass="bg-emerald-500"
        />
        <StatCard
          label="In Progress"
          value={orders.filter(o => ["Pending", "Approved", "Packed"].includes(o.status)).length}
          icon={Clock}
          colorClass="bg-amber-500"
        />
        <StatCard
          label="Delayed"
          value={orders.filter(o => o.status === "Delayed").length}
          icon={AlertTriangle}
          colorClass="bg-red-500"
        />
      </div>

      {/* Toolbar & Filter */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 transition-all placeholder:text-slate-400 text-sm"
            placeholder="Search by Order ID or Customer..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-48">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 appearance-none cursor-pointer"
            >
              <option value="All">All Statuses</option>
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
              ▼
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Items</th>
                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Value</th>
                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    No orders match your search.
                  </td>
                </tr>
              ) : filteredOrders.map((order) => (
                <tr key={order.docId} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-6 py-4 text-sm font-medium text-navy-900">{order.docId}</td>
                  <td className="px-6 py-4 text-sm text-slate-700">{order.customer}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <div className="max-w-xs">
                      {(order.items || []).map((item, i) => (
                        <div key={i} className="truncate">
                          <span className="font-medium text-slate-900">{item.qty}x</span> {item.description || item.materialNo}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(order.status)}
                    {order.status === "Rejected" && order.rejectionReason && (
                      <div className="text-xs text-red-500 mt-1 italic max-w-[150px] truncate" title={order.rejectionReason}>
                        {order.rejectionReason}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-slate-700">
                    ${getTotalValue(order.items).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {Array.isArray(STATUS_CONFIG[order.status]?.next) &&
                        STATUS_CONFIG[order.status].next.map((nextStatus) => (
                          <button
                            key={nextStatus}
                            onClick={() => updateStatus(order, nextStatus)}
                            disabled={loadingId === order.docId}
                            className={`p-1.5 rounded transition-colors ${nextStatus === 'Approved' ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' :
                              nextStatus === 'Rejected' ? 'bg-red-50 text-red-600 hover:bg-red-100' :
                                'bg-blue-50 text-blue-600 hover:bg-blue-100'
                              }`}
                            title={`Mark as ${nextStatus}`}
                          >
                            {nextStatus === 'Approved' ? <CheckCircle size={16} /> :
                              nextStatus === 'Rejected' ? <XCircle size={16} /> :
                                <span className="text-xs font-medium px-1">{nextStatus}</span>}
                          </button>
                        ))}
                      <button
                        onClick={() => deleteOrder(order)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete Order"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="font-bold text-navy-900">Reject Order</h3>
              <button onClick={() => setShowRejectPrompt(null)} className="text-slate-400 hover:text-red-500 font-bold">&times;</button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Rejection Reason</label>
              <textarea
                rows={4}
                value={rejectMessage}
                onChange={(e) => setRejectMessage(e.target.value)}
                className="w-full p-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-sm"
                placeholder="Please provide a reason for rejecting this order..."
              />
              <div className="flex justify-end mt-6 gap-3">
                <button
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium transition-colors"
                  onClick={() => setShowRejectPrompt(null)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium shadow-lg shadow-red-600/20 transition-colors"
                  onClick={confirmRejection}
                >
                  Reject Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
